import { useCallback, useEffect, useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { Card } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input, Select } from "../components/ui/Fields";
import { ConfirmDialog } from "../components/ui/Modal";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";

/**
 * Round 50 — Forbidden Words over routes/admin/forbidden-words.js. The
 * filtering itself happens in the database (triggers on every user-text
 * write), so a change here applies immediately to all users with no app
 * update. Matching is case-insensitive; "Whole word" (default) never matches
 * inside a longer word, "Also inside words" does.
 */
interface Word { id: number; word: string; match_mode: "word" | "contains"; enabled: boolean }

export function ForbiddenWordsPage() {
  const [words, setWords] = useState<Word[]>([]);
  const [search, setSearch] = useState("");
  const [enabledFilter, setEnabledFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [newWord, setNewWord] = useState("");
  const [newMode, setNewMode] = useState<"word" | "contains">("word");
  const [editId, setEditId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [editMode, setEditMode] = useState<"word" | "contains">("word");
  const [toDelete, setToDelete] = useState<Word | null>(null);
  const [testText, setTestText] = useState("");
  const [testResult, setTestResult] = useState<string | null>(null);
  const { showToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest<{ words: Word[] }>("/admin/forbidden-words", {
        params: { ...(search.trim() ? { search: search.trim() } : {}), ...(enabledFilter ? { enabled: enabledFilter } : {}) },
      });
      setWords(res.words);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not load words", "error");
    } finally {
      setLoading(false);
    }
  }, [search, enabledFilter, showToast]);
  useEffect(() => { void load(); }, [load]);

  async function add() {
    try {
      await apiRequest("/admin/forbidden-words", { method: "POST", body: { word: newWord, matchMode: newMode } });
      setNewWord("");
      showToast("Word added", "success");
      await load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not add", "error");
    }
  }
  async function patch(id: number, body: Record<string, unknown>) {
    try {
      await apiRequest(`/admin/forbidden-words/${id}`, { method: "PATCH", body });
      await load();
      return true;
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update", "error");
      return false;
    }
  }
  async function remove() {
    if (!toDelete) return;
    try {
      await apiRequest(`/admin/forbidden-words/${toDelete.id}`, { method: "DELETE" });
      showToast("Word deleted", "success");
      setToDelete(null);
      await load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete", "error");
    }
  }
  async function runTest() {
    try {
      const res = await apiRequest<{ result: string }>("/admin/forbidden-words/test", { method: "POST", body: { text: testText } });
      setTestResult(res.result);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Test failed", "error");
    }
  }

  return (
    <AppShell title="Forbidden Words">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        Matching text is replaced with **** in private chat, room chat, usernames, room names and announcements, About/signature,
        posts and comments. It is case-insensitive and applied by the server, so it cannot be bypassed from the app. Changes apply
        immediately.
      </p>

      <Card className="mb-4 max-w-2xl p-5">
        <div className="flex items-end gap-2">
          <Input label="Add word or phrase" value={newWord} onChange={(e) => setNewWord(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && newWord.trim()) void add(); }} />
          <Select label="Match" value={newMode} onChange={(e) => setNewMode(e.target.value as "word" | "contains")}>
            <option value="word">Whole word</option>
            <option value="contains">Also inside words</option>
          </Select>
          <Button onClick={add} disabled={!newWord.trim()}>Add</Button>
        </div>
      </Card>

      <Card className="mb-4 max-w-2xl p-5">
        <div className="mb-3 flex items-end gap-2">
          <Input label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select label="Status" value={enabledFilter} onChange={(e) => setEnabledFilter(e.target.value)}>
            <option value="">All</option>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </Select>
        </div>
        {loading ? <p className="text-sm text-ink-faint">Loading…</p> : words.length === 0 ? <p className="text-sm text-ink-faint">No words.</p> : (
          <ul className="divide-y divide-surface-border">
            {words.map((w) => (
              <li key={w.id} className="flex items-center gap-2 py-2">
                {editId === w.id ? (
                  <>
                    <Input value={editText} onChange={(e) => setEditText(e.target.value)} />
                    <Select value={editMode} onChange={(e) => setEditMode(e.target.value as "word" | "contains")}>
                      <option value="word">Whole word</option>
                      <option value="contains">Also inside words</option>
                    </Select>
                    <Button onClick={async () => { if (await patch(w.id, { word: editText, matchMode: editMode })) setEditId(null); }} disabled={!editText.trim()}>Save</Button>
                    <Button variant="secondary" onClick={() => setEditId(null)}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <span className={`flex-1 text-sm ${w.enabled ? "text-ink" : "text-ink-faint line-through"}`}>{w.word}</span>
                    <span className="text-xs text-ink-faint">{w.match_mode === "word" ? "whole word" : "inside words"}</span>
                    <Button variant="secondary" onClick={() => void patch(w.id, { enabled: !w.enabled })}>{w.enabled ? "Disable" : "Enable"}</Button>
                    <Button variant="secondary" onClick={() => { setEditId(w.id); setEditText(w.word); setEditMode(w.match_mode); }}>Edit</Button>
                    <Button variant="danger" onClick={() => setToDelete(w)}>Delete</Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="max-w-2xl p-5">
        <div className="flex items-end gap-2">
          <Input label="Try the filter" placeholder="hello BADWORD world" value={testText} onChange={(e) => { setTestText(e.target.value); setTestResult(null); }} />
          <Button variant="secondary" onClick={runTest} disabled={!testText}>Test</Button>
        </div>
        {testResult !== null && <p className="mt-2 text-sm text-ink">Result: <span className="font-mono">{testResult}</span></p>}
      </Card>

      <ConfirmDialog
        isOpen={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={remove}
        isDangerous
        title="Delete forbidden word"
        confirmLabel="Delete"
        description={toDelete ? `Delete "${toDelete.word}"? It will no longer be masked.` : ""}
      />
    </AppShell>
  );
}
